import React, { useState, useEffect } from 'react';
import { Row, Col, Typography, Card, Spin, Alert } from 'antd';
import MainLayout from '../../layouts/MainLayout';
import { financialApi } from '../../utils/financialApi';
import { extractApiUserMessage } from '../../utils/api';
import { formatPriceToman } from '../../utils/formatPriceToman';
import { DollarOutlined, ExclamationCircleOutlined, WarningOutlined, InboxOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;

const ReceivablesSummary = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const res = await financialApi.getReceivablesSummary();
        const receivablesSummary = res?.data?.receivables_summary || {};
        setData(receivablesSummary);
      } catch (err) {
        setError(extractApiUserMessage(err) || 'خطا در دریافت اطلاعات وصول مطالبات');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <MainLayout>
        <div className="flex justify-center items-center h-[60vh]">
          <Spin size="large" />
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="p-4 md:p-6 max-w-[1600px] mx-auto animate-fade-in">
        <div className="mb-6">
          <Title level={3} className="m-0 text-gray-800 dark:text-gray-100 font-bold">
            گزارش وصول مطالبات و چک‌های در جریان
          </Title>
          <Text className="text-gray-500 dark:text-gray-400">
            خلاصه وضعیت مطالبات شرکت و چک‌های صیادی
          </Text>
        </div>

        {error && (
          <Alert
            message="خطا"
            description={error}
            type="error"
            showIcon
            className="mb-6"
          />
        )}

        {data && (
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} lg={6}>
              <Card className="shadow-sm rounded-2xl dark:bg-gray-800">
                <div className="flex justify-between items-center">
                  <div>
                    <Text className="text-gray-500 block mb-2">کل اقساط در انتظار وصول</Text>
                    <Title level={4} className="m-0 text-blue-500">
                      {formatPriceToman(data.total_installments_pending)}
                    </Title>
                  </div>
                  <div className="w-12 h-12 bg-blue-50 text-blue-500 rounded-full flex items-center justify-center text-xl">
                    <DollarOutlined />
                  </div>
                </div>
              </Card>
            </Col>
            
            <Col xs={24} sm={12} lg={6}>
              <Card className="shadow-sm rounded-2xl dark:bg-gray-800">
                <div className="flex justify-between items-center">
                  <div>
                    <Text className="text-gray-500 block mb-2">چک‌های نزد صندوق</Text>
                    <Title level={4} className="m-0 text-emerald-500">
                      {formatPriceToman(data.total_cheques_in_vault)}
                    </Title>
                  </div>
                  <div className="w-12 h-12 bg-emerald-50 text-emerald-500 rounded-full flex items-center justify-center text-xl">
                    <InboxOutlined />
                  </div>
                </div>
              </Card>
            </Col>

            <Col xs={24} sm={12} lg={6}>
              <Card className="shadow-sm rounded-2xl dark:bg-gray-800">
                <div className="flex justify-between items-center">
                  <div>
                    <Text className="text-gray-500 block mb-2">چک‌های برگشتی</Text>
                    <Title level={4} className="m-0 text-red-500">
                      {formatPriceToman(data.total_cheques_bounced)}
                    </Title>
                  </div>
                  <div className="w-12 h-12 bg-red-50 text-red-500 rounded-full flex items-center justify-center text-xl">
                    <ExclamationCircleOutlined />
                  </div>
                </div>
              </Card>
            </Col>

            <Col xs={24} sm={12} lg={6}>
              <Card className="shadow-sm rounded-2xl dark:bg-gray-800">
                <div className="flex justify-between items-center">
                  <div>
                    <Text className="text-gray-500 block mb-2">مطالبات در خطر</Text>
                    <Title level={4} className="m-0 text-amber-500">
                      {formatPriceToman(data.total_receivables_at_risk)}
                    </Title>
                  </div>
                  <div className="w-12 h-12 bg-amber-50 text-amber-500 rounded-full flex items-center justify-center text-xl">
                    <WarningOutlined />
                  </div>
                </div>
              </Card>
            </Col>
          </Row>
        )}
      </div>
    </MainLayout>
  );
};

export default ReceivablesSummary;
